// src/App.js
import React, { lazy, Suspense, useContext, useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { Helmet } from "react-helmet";
import { UserProvider, UserContext } from "./components/userContext";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import Nav from "./components/nav";
import Footer from "./components/footer";
import GlobalNotification from "./components/globalNotification";

import Home from "./components/home";
import Signup from "./components/signup";
import Signin from "./components/signin";
import ForgotPassword from "./components/forgotPassword";
import ResetPassword from "./components/resetPassword";
import OAuthSuccess from "./components/OAuthSuccess";

import RequireAdmin from "./components/UMS/GeneralManager/requireAdmin";

import {
  isIndexableMarketingPath,
  isMarketingPath,
  isPubliclyAccessiblePath,
  normalizePublicPath,
  shouldIncludeCanonical,
} from "./components/public/publicRoutes";
import { PUBLIC_RELEASE_ID } from "./components/public/publicSiteRegistry.mjs";
import { usePageAnalytics } from "./components/hooks/usePageAnalytics";
import { backendUrl } from "./components/config";

import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "./App.css";

import PrivacyPolicy from "./components/legal/privacyPolicy.jsx";
import TermsOfService from "./components/legal/termsOfService.jsx";

const INITIAL_DOCUMENT_RELEASE_ID =
  typeof document === "undefined"
    ? ""
    : document
        .querySelector('meta[name="taskersys-public-release"]')
        ?.getAttribute("content") || "";

const APP_THEME = createTheme({
  palette: {
    primary: {
      main: "#4b9ef1",
      dark: "#2f78c8",
      light: "#edf6ff",
    },
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { textTransform: "none" },
      },
    },
  },
});

const QUERY_CLIENT = new QueryClient();

// Public pages other than the homepage are loaded only when requested.
const About = lazy(() => import("./components/about"));
const Services = lazy(() => import("./components/public/Services"));
const ServiceDetail = lazy(() => import("./components/public/ServiceDetail"));
const Platform = lazy(() => import("./components/public/Platform"));
const Contact = lazy(() => import("./components/public/Contact"));
const Projects = lazy(() => import("./components/public/Projects"));
const NotFound = lazy(() => import("./components/public/NotFound"));

// Load route components when their page is opened.
const Client = lazy(() => import("./components/client"));
const Manager = lazy(() => import("./components/manager"));
const Tasker = lazy(() => import("./components/tasker"));
const Implementer = lazy(() => import("./components/implementer"));
const CreateTicket = lazy(() => import("./components/createTicket"));
const Dashboard = lazy(() => import("./components/dashboard"));
const Reporting = lazy(() => import("./components/reporting"));
const ImplementerSchedule = lazy(() => import("./components/implementerSchedule"));
const Timesheet = lazy(() => import("./components/timesheet"));
const TimesheetSummary = lazy(() => import("./components/timesheetSummary"));
const PNL = lazy(() => import("./components/PNL")); // Gross P&L per Project (kept at /pnl)

const RegistrationForm = lazy(() => import("./components/UMS/GeneralManager/registrationForm"));
const GeneralManagerDashboard = lazy(() => import("./components/UMS/GeneralManager/generalManagerDashboard"));
const StandardServiceSetupPanel = lazy(() => import("./components/UMS/GeneralManager/standardSetviceSetupPanel"));
const StandardTaskPanelsDashboard = lazy(() => import("./components/UMS/GeneralManager/standardTaskPanelsDashboard"));
const TaskerFinancialPerformanceDashboard = lazy(() => import("./components/UMS/GeneralManager/taskerFinancialPerformanceDashboard"));
const AdminPanel = lazy(() => import("./components/UMS/Admin/adminPanel"));
const AdminAnalyticsDashboard = lazy(() => import("./components/UMS/Admin/adminAnalyticsDashboard"));
const VisitorDetails = lazy(() => import("./components/UMS/Admin/visitorDetails"));
const AzureBlobCleaner = lazy(() => import("./components/azureBlobCleaner"));
const ReceiptsDashboard = lazy(() => import("./components/receiptsDashboard"));
const PayrollDashboard = lazy(() => import("./components/payrollDashboard"));
const ImplementerPayments = lazy(() => import("./components/implementerPayments"));
const StandardContractsDashboard = lazy(() => import("./components/UMS/GeneralManager/standardContractsDashboard"));
const InvoiceGeneratorMultipleTasks = lazy(() => import("./components/invoiceGeneratorMultipleTasks.jsx"));
const ProfileView = lazy(() => import("./components/marketplace/profileView"));
const ProfileEdit = lazy(() => import("./components/marketplace/profileEdit"));
const ReceivableLedger = lazy(() => import('./components/receivableLedger'));
const PayableLedger = lazy(() => import('./components/payableLedger'));
const GeneralManagerInvoiceSettings = lazy(() => import('./components/UMS/GeneralManager/settings.jsx'));
const TaskerPayroll = lazy(() => import('./components/taskerPayroll'));
const TaskerPayrollDashboard = lazy(() => import('./components/taskerPayrollDashboard'));
const TaskerPayableLedger = lazy(() => import("./components/taskerPayableLedger.jsx"));
const Invoice = lazy(() => import("./components/invoice"));

const Marketplace = lazy(() => import("./components/marketplace/marketplace"));
const UserProfileView = lazy(() => import("./components/marketplace/userProfileView"));
const ServiceProviderProfileView = lazy(() => import("./components/marketplace/serviceProviderProfileView"));

const PayrollGenerator = lazy(() => import("./components/payrolGenerator")); // (intentionally: payrolGenerator)
const Invoices = lazy(() => import("./components/invoices"));

const TaskerInvoiceGenerator = lazy(() => import("./components/taskerInvoiceGenerator.jsx"));
const TaskerInvoices = lazy(() => import("./components/taskerInvoices.jsx"));
const TaskerReceivableLedger = lazy(() => import("./components/taskerReceivableLedger.jsx"));

const InvitationToBidForm = lazy(() => import("./components/invitationToBid/invitationToBidForm"));
const BidDashboard = lazy(() => import("./components/invitationToBid/bidDashboard"));
const SubmitOffer = lazy(() => import("./components/invitationToBid/submitOffer"));
const BidDetail = lazy(() => import("./components/invitationToBid/bidDetail"));

const TransactionsIngest = lazy(() => import("./components/bookKeeping/transactionsIngest"));
const TransactionsDashboard = lazy(() => import("./components/bookKeeping/transactionsDashboard.jsx"));
const BankLedger = lazy(() => import("./components/bookKeeping/bankLedger.jsx"));
const CreditCardBankReconsiliation = lazy(() => import("./components/creditCardBankReconsiliation.jsx"));
const PayableReconciliation = lazy(() => import("./components/bookKeeping/payableReconciliation.jsx"));

/* ===== NEW: Accounting components you already have ===== */
const CategoryLedger = lazy(() => import("./components/accounting/CategoryLedger.jsx"));
const JournalEntryPage = lazy(() => import("./components/accounting/journalEntry.jsx"));
const PostingSimulator = lazy(() => import("./components/accounting/postingEngine.jsx"));
const BankReconciliation = lazy(() => import("./components/bookKeeping/bankReconciliation.jsx"));
const TrialBalance = lazy(() => import("./components/accounting/trialBalance.jsx"));
const ProfitLoss = lazy(() => import("./components/accounting/profitLoss.jsx"));
const BalanceSheet = lazy(() => import("./components/accounting/balanceSheet.jsx"));
const TaskerSetting = lazy(() => import("./components/UMS/Tasker/taskerSetting.jsx"));
const OpenCloseConsole = lazy(() => import("./components/accounting/openCloseConsole.jsx"));
const FindPost = lazy(() => import("./components/crawler/findPost.jsx"));
const BuildingsMaintenanceDashboard = lazy(() => import('./components/buildingMaintenance/buildingsMaintenanceDashboard.jsx'));
const BuildingMaintenancePage = lazy(() => import('./components/buildingMaintenance/buildingMaintenancePage.jsx'));
const MaintenanceCalendarPage = lazy(() => import("./components/buildingMaintenance/maintenanceCalendarPage.jsx"));
const BuildingsTableContainer = lazy(() => import("./components/UMS/GeneralManager/BuildingsTableContainer.jsx"));
const MoveInWizard = lazy(() => import("./components/moveIn/moveInWizard.jsx")); 
const RentalUnitsRoomsDashboard = lazy(() => import("./components/moveIn/rentalUnitsRoomsDashboard.jsx"));
const JournalExplorer = lazy(() => import("./components/accounting/journalExplorer.jsx"));
const BankAccounts = lazy(() => import("./components/bookKeeping/bankAccounts.jsx"));
const ImplementerAdvances = lazy(() => import("./components/implementerAdvances"));
const GalleryManager = lazy(() => import("./components/UMS/GeneralManager/Gallery/galleryManager.jsx"));
const QuoteGenerator = lazy(() => import("./components/quoteGenerator.jsx"));
const FinancialReportCenter = lazy(() => import("./components/accounting/financialReportCenter.jsx"));
const RecurringCharges = lazy(() => import('./components/recurringCharges'));
const ManualBulkTickets = lazy(() => import("./components/UMS/Tasker/manualBulkTickets.jsx"));
const ExpectedPayableBalance = lazy(() => import("./components/expectedPayableBalance.jsx"));
const MonthlyPayrollExpenses = lazy(() => import("./components/monthlyPayrollExpenses.jsx"));
const MonthlyReceivableSummary = lazy(() => import("./components/monthlyReceivableSummary.jsx"));
const InvoiceReconciliation = lazy(() => import("./components/invoiceReconciliation.jsx"));
const BusinessExpensesDashboard = lazy(() => import("./components/bookKeeping/businessExpenses/businessExpensesDashboard.jsx"));
const MonthlyReceiptExpenses = lazy(() => import("./components/monthlyReceiptExpenses.jsx"));
const DirectExpenses = lazy(() => import("./components/directExpenses.jsx"));
const ImplementerFinancials = lazy(() => import("./components/implementerFinancials.jsx"));
const DirectExpensesByTicket = lazy(() => import("./components/directExpensesByTicket.jsx"));
const OpenTicketCashPlanning = lazy(() => import("./components/openTicketCashPlanning.jsx"));
const UninvoicedCompletedTicketsAudit = lazy(() => import("./components/uninvoicedCompletedTicketsAudit.jsx"));
const TicketProjectPayrollAudit = lazy(() => import("./components/ticketProjectPayrollAudit.jsx"));
const ExternalPayablesDashboard = lazy(() => import("./components/bookKeeping/externalPayables/externalPayablesDashboard.jsx"));
const CashInflow = lazy(() => import("./components/cashInFlow.jsx"));
const CashOutFlow = lazy(() => import("./components/cashOutFlow.jsx"));
const GrowthIntelligence = lazy(() => import("./components/growthIntelligence/growthIntelligence.jsx"));

// Keep navigation available if a deferred page cannot be loaded.
class RouteErrorBoundary extends React.Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    console.error("Unable to load the requested page:", error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="py-3 text-center" role="alert">
          <p>Unable to load this page. Please reload to try again.</p>
          <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>
            Reload page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

/* ===== Lightweight placeholders so routes work today ===== */
const Placeholder = ({ title, note }) => (
  <div className="py-3">
    <h4 className="mb-2">{title}</h4>
    {note ? <p className="text-muted">{note}</p> : null}
  </div>
);

const ChartOfAccountsPage = () => (
  <Placeholder
    title="Chart of Accounts"
    note="Hook this page to your Account model CRUD. For now this is a placeholder route."
  />
);
const BankReconciliationPage = () => (
  <Placeholder
    title="Bank Reconciliation"
    note="Match BankTransactionManual lines to Journal Entries. Placeholder route."
  />
);

// NEW: Tenant management placeholders (until you build full pages)
const TenantsPage = () => (
  <Placeholder
    title="Tenants"
    note="Tenant list, search, and quick actions will appear here."
  />
);

const LeasesPage = () => (
  <Placeholder
    title="Leases"
    note="Lease list, filters, and status overview will appear here."
  />
);

const ChargesPage = () => (
  <Placeholder
    title="Tenant Charges"
    note="Rent and fee charges per tenant/lease will appear here."
  />
);

const TenantPaymentsPage = () => (
  <Placeholder
    title="Tenant Payments"
    note="Payment history and reconciliation for tenants will appear here."
  />
);

/* ---------------- Wrappers (kept; needed for useParams) ---------------- */
const SetupWrapper = () => { const { setupId } = useParams(); return <StandardServiceSetupPanel setupId={setupId} />; };
const ClientWrapper = () => { const { ticketID } = useParams(); return <Client ticketID={ticketID} />; };
const ManagerWrapper = () => { const { ticketID } = useParams(); return <Manager ticketID={ticketID} key={ticketID} />; };
const TaskerWrapper = () => { const { ticketID } = useParams(); return <Tasker ticketID={ticketID} />; };
const ImplementerWrapper = () => { const { ticketID } = useParams(); return <Implementer ticketID={ticketID} />; };
const InvoiceRoute = () => { const { invoiceNumber } = useParams(); return <Invoice invoiceNumber={invoiceNumber} />; };
const UserProfileWrapper = () => { const { id } = useParams(); return <UserProfileView backendUrl={backendUrl} userId={id} />; };
const ServiceProviderProfileWrapper = () => { const { id } = useParams(); return <ServiceProviderProfileView backendUrl={backendUrl} spId={id} />; };

/* ---------------- Role helpers (normalized) ---------------- */
const normalizeRole = (s) => String(s ?? "").toLowerCase().trim();
const getRole = (user) => normalizeRole(user?.usertype ?? user?.userType);

/* ---------------- Auth guard: render loader while initializing ---------------- */
const ProtectedRoute = ({ allowedUserTypes, children }) => {
  const { user, isUserLoaded } = useContext(UserContext);
  if (!isUserLoaded) return <div style={{ textAlign: "center", padding: "2rem" }}>Loading…</div>;
  const role = getRole(user);
  const allowed = (allowedUserTypes || []).map(normalizeRole);
  if (!user) return <Navigate to="/signin" replace />;
  if (allowed.length && !allowed.includes(role)) return <Navigate to="/dashboard" replace />;
  return children;
};

const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isUserLoaded, updateUser } = useContext(UserContext);
  const role = getRole(user);
  const pathname = normalizePublicPath(location.pathname);
  const isMarketingPage = isMarketingPath(pathname);
  const hostname =
    typeof window === "undefined"
      ? "taskersys.com"
      : window.location.hostname.toLowerCase();
  const isTaskerSysHost =
    hostname === "taskersys.com" ||
    hostname === "www.taskersys.com" ||
    hostname === "localhost" ||
    hostname === "127.0.0.1";
  const isIndexablePage =
    isTaskerSysHost && isIndexableMarketingPath(pathname);
  const canonicalUrl = `https://taskersys.com${pathname === "/" ? "/" : pathname}`;
  const robotsContent = isIndexablePage
    ? "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1"
    : "noindex,nofollow";
  const includeCanonical =
    isTaskerSysHost && shouldIncludeCanonical(pathname);

  usePageAnalytics();

  /* ---------------- Public release synchronization check ---------------- */
  useEffect(() => {
    if (typeof document === "undefined") return;

    document.documentElement.dataset.taskersysRelease = PUBLIC_RELEASE_ID;

    const initialRelease = String(INITIAL_DOCUMENT_RELEASE_ID || "").trim();
    const isPlaceholder = initialRelease === "__TASKERSYS_PUBLIC_RELEASE__";

    if (isMarketingPage && initialRelease && !isPlaceholder && initialRelease !== PUBLIC_RELEASE_ID) {
      const mismatch = {
        serverRelease: initialRelease,
        frontendRelease: PUBLIC_RELEASE_ID,
        pathname: window.location.pathname,
      };

      window.__TASKERSYS_PUBLIC_RELEASE_MISMATCH__ = mismatch;
      console.error(
        "TaskerSys public release mismatch. The server HTML and React bundle were built from different releases.",
        mismatch
      );
    }
  }, [isMarketingPage]);

  /* ---------------- Canonical public host enforcement ----------------
     Direct requests are redirected by Express. This client-side safeguard
     covers SPA navigation from a private page on www or SuperTeam. */
  useEffect(() => {
    if (!isMarketingPage || typeof window === "undefined") return;

    const hostname = window.location.hostname.toLowerCase();
    const shouldRedirect =
      hostname === "www.taskersys.com" ||
      hostname === "superteamusa.com" ||
      hostname === "www.superteamusa.com";

    if (shouldRedirect) {
      window.location.replace(
        `https://taskersys.com${location.pathname}${location.search}${location.hash}`
      );
    }
  }, [isMarketingPage, location.pathname, location.search, location.hash]);

  /* ---------------- Session analytics beacon ---------------- */
  useEffect(() => {
    const start = Date.now();
    const reportDuration = () => {
      const durationSec = Math.floor((Date.now() - start) / 1000);
      const payload = JSON.stringify({ pagePath: window.location.pathname, sessionDuration: durationSec });
      const blob = new Blob([payload], { type: "application/json" });
      navigator.sendBeacon(`${backendUrl}/admin/analytics`, blob);
    };
    window.addEventListener("beforeunload", reportDuration);
    return () => { window.removeEventListener("beforeunload", reportDuration); reportDuration(); };
  }, []);

  /* ---------------- Initialize user session ---------------- */
  const didFetch = useRef(false);
  useEffect(() => {
    if (didFetch.current) return;
    didFetch.current = true;
    fetch(`${backendUrl}/me`, { credentials: "include" })
      .then(async (res) => { try { const data = await res.json(); return res.ok ? data : null; } catch { return null; } })
      .then((data) => {
        if (data?.user) {
          updateUser(data.user);
        } else {
          localStorage.removeItem("user");
          if (!isPubliclyAccessiblePath(window.location.pathname) && window.location.pathname !== "/signin") {
            window.location.href = "/signin";
          }
        }
      })
      .catch(() => {
        localStorage.removeItem("user");
        if (!isPubliclyAccessiblePath(window.location.pathname) && window.location.pathname !== "/signin") {
          window.location.href = "/signin";
        }
      });
  }, [updateUser]);

  /* ---------------- Clean up overlays between page changes ---------------- */
  useEffect(() => {
    try {
      document.body.classList.remove('modal-open');
      document.body.style.removeProperty('padding-right');
      document.querySelectorAll('.modal-backdrop').forEach((el) => { el.parentNode?.removeChild(el); });
    } catch {}
    if (Array.isArray(window.__tsysTimers)) {
      window.__tsysTimers.forEach((id) => { clearTimeout(id); clearInterval(id); });
      window.__tsysTimers = [];
    }
  }, [location.pathname]);

  /* ---------------- Route scrolling ----------------
     - Marketing hash links scroll to their section.
     - Normal route changes return to the top.
     Hash changes no longer remount the complete route tree. */
  const routeKey = `${location.pathname}${location.search}`;
  useEffect(() => {
    if (location.hash) {
      const targetId = decodeURIComponent(location.hash.slice(1));
      const target = document.getElementById(targetId);
      if (target) {
        window.requestAnimationFrame(() => {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
        });
        return;
      }
    }

    try {
      window.scrollTo({ top: 0, behavior: "instant" });
    } catch {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.search, location.hash]);

  if (!isUserLoaded && !isMarketingPage) {
    return <div style={{ textAlign: "center", padding: "2rem" }}>Loading…</div>;
  }

  return (
    <div className="d-flex flex-column min-vh-100">
      <Helmet>
        <meta name="taskersys-public-release" content={PUBLIC_RELEASE_ID} />
        {!isMarketingPage && includeCanonical ? (
          <link rel="canonical" href={canonicalUrl} />
        ) : null}
        {!isMarketingPage ? <meta name="robots" content={robotsContent} /> : null}
      </Helmet>

      <a className="taskersys-skip-link" href="#main-content">
        Skip to main content
      </a>

      <Nav />
      {user && !isMarketingPage && <GlobalNotification />}

      <main
        id="main-content"
        className={`flex-fill ${isMarketingPage ? "taskersys-public-main" : "container"}`}
        style={{ paddingTop: "0px" }}
      >
        <RouteErrorBoundary key={routeKey}>
          <Suspense fallback={<div style={{ textAlign: "center", padding: "2rem" }} role="status">Loading…</div>}>
        <Routes key={routeKey} location={location}>
          {/* Public */}
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/:serviceSlug" element={<ServiceDetail />} />
          <Route path="/platform" element={<Platform />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/signin" element={<Signin />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/oauth-success" element={<OAuthSuccess />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />

          {/* Entity wrappers */}
          <Route path="/client/:ticketID" element={<ClientWrapper />} />
          <Route path="/manager/:ticketID" element={<ManagerWrapper />} />
          <Route path="/tasker/:ticketID" element={<TaskerWrapper />} />
          <Route path="/implementer/:ticketID" element={<ImplementerWrapper />} />

          {/* Create ticket */}
          <Route
            path="/createTicket"
            element={
              <ProtectedRoute allowedUserTypes={["client", "manager", "general manager"]}>
                <CreateTicket />
              </ProtectedRoute>
            }
          />

          {/* Common dashboards */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reporting"
            element={
              <ProtectedRoute>
                <Reporting />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pnl"
            element={
              <ProtectedRoute allowedUserTypes={['tasker','manager','general manager','admin']}>
                <PNL />
              </ProtectedRoute>
            }
          />
          <Route path="/implementer-schedule" element={<ImplementerSchedule />} />
          <Route path="/timesheet" element={user ? <Timesheet key={user?.id || "guest"} /> : <Navigate to="/signin" replace />} />
          <Route path="/timesheetSummary" element={user ? <TimesheetSummary key={user?.id || "guest-summary"} /> : <Navigate to="/signin" replace />} />

          {/* Marketplace & Profiles */}
          <Route path="/marketplace" element={user ? (
            <Marketplace backendUrl={backendUrl} viewerType={role} currentUser={user} />
          ) : <Navigate to="/signin" replace />} />
          <Route path="/profile" element={user ? (
            <ProfileView backendUrl={backendUrl} canEdit onEdit={() => navigate("/profile/edit")} />
          ) : <Navigate to="/signin" replace />} />
          <Route path="/profile/edit" element={user ? (
            <ProfileEdit backendUrl={backendUrl} userId={user?._id || user?.id} user={user} onSaved={() => navigate("/profile")} />
          ) : <Navigate to="/signin" replace />} />
          <Route path="/profile/:id" element={user ? <UserProfileWrapper /> : <Navigate to="/signin" replace />} />
          <Route path="/sp/:id" element={user ? <ServiceProviderProfileWrapper /> : <Navigate to="/signin" replace />} />

          {/* Tasker-only tools */}
          <Route path="/tasker-invoice-generator" element={<ProtectedRoute allowedUserTypes={["tasker"]}><TaskerInvoiceGenerator /></ProtectedRoute>} />
          <Route path="/tasker-invoices" element={<ProtectedRoute allowedUserTypes={["tasker"]}><TaskerInvoices /></ProtectedRoute>} />
          <Route path="/tasker-receivable-ledger" element={<ProtectedRoute allowedUserTypes={["tasker"]}><TaskerReceivableLedger /></ProtectedRoute>} />
          <Route path="/tools/receipt-reconciliation" element={<ProtectedRoute allowedUserTypes={["tasker"]}><CreditCardBankReconsiliation /></ProtectedRoute>} />
          <Route path="/tools/payable-reconciliation" element={<ProtectedRoute allowedUserTypes={['tasker','manager','general manager','admin']}><PayableReconciliation/></ProtectedRoute>} />
          <Route path="/tools/bank-reconciliation" element={<ProtectedRoute allowedUserTypes={['tasker','manager','general manager','admin']}><BankReconciliation/></ProtectedRoute>} />

          {/* Shared tools */}
          <Route path="/invoice-generator-mt" element={<ProtectedRoute allowedUserTypes={['tasker', 'manager', 'general manager', 'admin']}><InvoiceGeneratorMultipleTasks /></ProtectedRoute>} />
          <Route path="/invoice-generator" element={<ProtectedRoute allowedUserTypes={['tasker', 'manager', 'general manager', 'admin']}><InvoiceGeneratorMultipleTasks /></ProtectedRoute>} />
          <Route path="/payroll-generator" element={<ProtectedRoute allowedUserTypes={["tasker"]}><PayrollGenerator /></ProtectedRoute>} />
          <Route path="/invoices" element={<ProtectedRoute allowedUserTypes={['tasker', 'manager', 'general manager', 'admin']}><Invoices /></ProtectedRoute>} />
          <Route path="/invoice/:invoiceNumber" element={<ProtectedRoute allowedUserTypes={['tasker', 'manager', 'general manager', 'admin']}><InvoiceRoute /></ProtectedRoute>} />
          <Route path="/payrolls" element={<ProtectedRoute allowedUserTypes={["tasker", "admin", "implementer"]}><PayrollDashboard /></ProtectedRoute>} />
          <Route path="/implementer-payments" element={<ProtectedRoute allowedUserTypes={["implementer"]}><ImplementerPayments /></ProtectedRoute>} />
          <Route path="/implementer-advances"   element={<ProtectedRoute allowedUserTypes={["implementer"]}><ImplementerAdvances /></ProtectedRoute>}/>
          <Route path="/receivable-ledger" element={<ProtectedRoute allowedUserTypes={['tasker', 'manager', 'general manager', 'admin']}><ReceivableLedger /></ProtectedRoute>} />

          {/* Finance */}
          <Route path="/tasker-payroll" element={<ProtectedRoute allowedUserTypes={['tasker','manager','general manager','admin']}><TaskerPayroll /></ProtectedRoute>} />
          <Route path="/tasker-payrolls" element={<ProtectedRoute allowedUserTypes={['tasker','manager','general manager','admin']}><TaskerPayrollDashboard /></ProtectedRoute>} />
          <Route path="/tasker-payable-ledger" element={<ProtectedRoute allowedUserTypes={['tasker','manager','general manager','admin']}><TaskerPayableLedger /></ProtectedRoute>} />
          <Route path="/bookkeeping/bank-ledger" element={<ProtectedRoute allowedUserTypes={['tasker','manager','general manager','admin']}><BankLedger /></ProtectedRoute>} />
          <Route path="/bookkeeping/bank-accounts" element={<ProtectedRoute allowedUserTypes={['tasker','manager','general manager', 'admin']}><BankAccounts /></ProtectedRoute>} />

          {/* Generic A/P */}
          <Route path="/payable-ledger" element={<PayableLedger />} />

          {/* UMS & Admin */}
          <Route path="/ums/registration-form" element={<RegistrationForm key={location.pathname} />} />
          <Route path="/ums/general-manager-dashboard" element={<GeneralManagerDashboard key={location.pathname} />} />
          <Route path="/ums/general-manager-settings" element={<ProtectedRoute allowedUserTypes={['general manager','admin']}><GeneralManagerInvoiceSettings/></ProtectedRoute>} />
          <Route path="/ums/general-manager-tasker-performance" element={<TaskerFinancialPerformanceDashboard key={location.pathname} />} />
          <Route path="/ums/general-manager-services" element={<StandardTaskPanelsDashboard key={location.pathname} />} />
          <Route path="/ums/general-manager-services/:setupId" element={<SetupWrapper />} />
          <Route path="/ums/manager-dashboard" element={<Dashboard key={location.pathname} />} />

          <Route path="/ums/general-manager-gallery" element={<GalleryManager key={location.pathname} />} />      

          <Route element={<RequireAdmin />}>
            <Route path="/ums/admin-panel" element={<AdminPanel key={location.pathname} />} />
            <Route path="/ums/admin-analytics" element={<AdminAnalyticsDashboard key={location.pathname} />} />
            <Route path="/ums/admin-analytics/:ip" element={<VisitorDetails key={location.pathname} />} />
            <Route path="/ums/admin-azure-cleaner" element={<AzureBlobCleaner key={location.pathname} />} />
            <Route path="/ums/admin-receipts" element={<ReceiptsDashboard key={location.pathname} />} />
          </Route>

          <Route path="/ums/tasker-receipts" element={<ProtectedRoute allowedUserTypes={["tasker", "admin"]}><ReceiptsDashboard key={location.pathname} /></ProtectedRoute>} />
          <Route path="/ums/standard-contracts" element={<ProtectedRoute allowedUserTypes={["general manager", "admin"]}><StandardContractsDashboard key={location.pathname} /></ProtectedRoute>} />

          {/* Bids */}
          <Route path="/bids" element={<ProtectedRoute allowedUserTypes={['tasker','manager','general manager','admin','implementer','client']}><BidDashboard /></ProtectedRoute>} />
          <Route path="/ingest/transactions" element={<ProtectedRoute allowedUserTypes={['tasker','manager','general manager','admin','implementer']}><TransactionsIngest /></ProtectedRoute>} />
          <Route path="/bookkeeping/transactions" element={<ProtectedRoute allowedUserTypes={['tasker','manager','general manager','admin','implementer']}><TransactionsDashboard /></ProtectedRoute>} />
          <Route path="/bids/new" element={<ProtectedRoute allowedUserTypes={['tasker','manager','general manager','admin']}><InvitationToBidForm /></ProtectedRoute>} />
          <Route path="/bids/:id" element={<ProtectedRoute allowedUserTypes={['tasker','manager','general manager','admin','implementer','client']}><BidDetail /></ProtectedRoute>} />
          <Route path="/bids/:id/submit-offer" element={<ProtectedRoute allowedUserTypes={['implementer','manager','general manager','admin','tasker']}><SubmitOffer /></ProtectedRoute>} />

          <Route path="/generateQuote" element={<ProtectedRoute allowedUserTypes={['tasker','manager','general manager','admin']}><QuoteGenerator /></ProtectedRoute>}/>

          {/* ===== UPDATED: Accounting routes ===== */}
          <Route
            path="/accounting/coa"
            element={
              <ProtectedRoute allowedUserTypes={['tasker','manager','general manager','admin']}>
                <ChartOfAccountsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounting/journal-entries"
            element={
              <ProtectedRoute allowedUserTypes={['tasker','manager','general manager','admin']}>
                <JournalEntryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounting/categories"
            element={
              <ProtectedRoute allowedUserTypes={['tasker','manager','general manager','admin']}>
                <CategoryLedger />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounting/posting"
            element={
              <ProtectedRoute allowedUserTypes={['tasker','manager','general manager','admin']}>
                <PostingSimulator />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounting/reconciliation"
            element={
              <ProtectedRoute allowedUserTypes={['tasker','manager','general manager','admin']}>
                <BankReconciliationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounting/journal-explorer"
            element={
              <ProtectedRoute allowedUserTypes={['tasker','manager','general manager','admin']}>
                <JournalExplorer/>
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounting/trial-balance"
            element={
              <ProtectedRoute allowedUserTypes={['tasker','manager','general manager','admin']}>
                <TrialBalance />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounting/pl"
            element={
              <ProtectedRoute allowedUserTypes={['tasker','manager','general manager','admin']}>
                <ProfitLoss />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounting/balance-sheet"
            element={
              <ProtectedRoute allowedUserTypes={['tasker','manager','general manager','admin']}>
                <BalanceSheet />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounting/tasker-accounting-setting"
            element={
              <ProtectedRoute allowedUserTypes={['tasker']}>
                <TaskerSetting />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounting/open-close-balances"
            element={
              <ProtectedRoute allowedUserTypes={['tasker','manager','general manager','admin']}>
                <OpenCloseConsole />
              </ProtectedRoute>
            }
          />
          <Route
            path="/find-post"
            element={
              <ProtectedRoute allowedUserTypes={['tasker','manager','general manager','admin','implementer','client']}>
                <FindPost />
              </ProtectedRoute>
            }
          />
          <Route
            path="/buildings"
            element={
              <ProtectedRoute allowedUserTypes={['manager','general manager','admin']}>
                <BuildingsTableContainer />
              </ProtectedRoute>
            }
          />

          {/* Maintenance selector (list of buildings → jump to /buildings/:id/maintenance) */}
          <Route
            path="/buildings/maintenance"
            element={
              <ProtectedRoute allowedUserTypes={['manager','general manager','admin']}>
                <BuildingsMaintenanceDashboard />
              </ProtectedRoute>
            }
          />

          {/* Per-building maintenance page (profile + assets + schedule) */}
          <Route
            path="/buildings/:id/maintenance"
            element={
              <ProtectedRoute allowedUserTypes={['manager','general manager','admin']}>
                <BuildingMaintenancePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/maintenance/calendar"
            element={
              <ProtectedRoute allowedUserTypes={['manager','general manager','admin']}>
                <MaintenanceCalendarPage />
              </ProtectedRoute>
            }
          />

          {/* Tenant Management (Manager / General Manager only) */}
          <Route
            path="/tenants"
            element={
              <ProtectedRoute allowedUserTypes={['manager','general manager']}>
                <TenantsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leases"
            element={
              <ProtectedRoute allowedUserTypes={['manager','general manager']}>
                <LeasesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/charges"
            element={
              <ProtectedRoute allowedUserTypes={['manager','general manager']}>
                <ChargesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tenant-payments"
            element={
              <ProtectedRoute allowedUserTypes={['manager','general manager']}>
                <TenantPaymentsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/move-in"
            element={
              <ProtectedRoute allowedUserTypes={['manager','general manager']}>
                <MoveInWizard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rental-dashboard"
            element={
              <ProtectedRoute allowedUserTypes={['manager','general manager']}>
                <RentalUnitsRoomsDashboard />
              </ProtectedRoute>
            }
          />
                 <Route
           path="/accounting/financial-reports"
           element={
            <ProtectedRoute allowedUserTypes={['tasker','manager','general manager','admin']}>
           <FinancialReportCenter />
           </ProtectedRoute>}
          />
         <Route
  path="/recurring-charges"
  element={
    <ProtectedRoute allowedUserTypes={['tasker','manager','general manager','admin']}>
      <RecurringCharges />
    </ProtectedRoute>
  }
/>
                {/* Manual Bulk Tickets */}
          <Route
            path="/manual-bulk-tickets"
            element={
              <ProtectedRoute allowedUserTypes={['tasker']}>
                <ManualBulkTickets />
              </ProtectedRoute>
            }
          />
                 {/* expected payable balance */}
          <Route
            path="/expected-payable-balances"
            element={
              <ProtectedRoute allowedUserTypes={['tasker']}>
                <ExpectedPayableBalance/>
              </ProtectedRoute>
            }
          />
{/* implementer expenses */}
                  <Route
            path="/imp-expenses"
            element={
              <ProtectedRoute allowedUserTypes={['tasker']}>
                <MonthlyPayrollExpenses/>
              </ProtectedRoute>
            }
          />
                {/* invoices summary */}
                  <Route
            path="/invoices-summary"
            element={
              <ProtectedRoute allowedUserTypes={['manager','general manager']}>
                <MonthlyReceivableSummary/>
              </ProtectedRoute>
            }
          />
                {/* invoices reconciliation */}
                 <Route
            path="/invoice-=reconciliation"
            element={
              <ProtectedRoute allowedUserTypes={['manager','general manager']}>
                <InvoiceReconciliation/>
              </ProtectedRoute>
            }
          />
                {/* business transactions */}
                   <Route
            path="/bookkeeping/business-receipts"
            element={
              <ProtectedRoute allowedUserTypes={['manager','general manager']}>
                <BusinessExpensesDashboard/>
              </ProtectedRoute>
            }
          />
                {/* receipts materials expenses */}
                  <Route
            path="/mat-expenses"
            element={
              <ProtectedRoute allowedUserTypes={['tasker']}>
                <MonthlyReceiptExpenses/>
              </ProtectedRoute>
            }
          />
            {/* direct expenses */}
                 <Route
            path="/dir-expenses"
            element={
              <ProtectedRoute allowedUserTypes={['tasker']}>
                <DirectExpenses/>
              </ProtectedRoute>
            }
          />
                  {/* implementer finacials */}
                 <Route
            path="/imp-financials"
            element={
              <ProtectedRoute allowedUserTypes={['tasker']}>
                <ImplementerFinancials/>
              </ProtectedRoute>
            }
          />
                  {/* direct expenses by tickets */}
                 <Route
            path="/dir-expenses-tickets"
            element={
              <ProtectedRoute allowedUserTypes={['tasker']}>
                <DirectExpensesByTicket/>
              </ProtectedRoute>
            }
          />
                 {/* expected expenses */}
                 <Route
            path="/expected-expenses"
            element={
              <ProtectedRoute allowedUserTypes={['tasker']}>
                <OpenTicketCashPlanning/>
              </ProtectedRoute>
            }
          />
                 {/* audit of uninvoiced tickets */}
                 <Route
            path="/audit-uninvoiced-tickets"
            element={
              <ProtectedRoute allowedUserTypes={['manager','general manager']}>
                <UninvoicedCompletedTicketsAudit/>
              </ProtectedRoute>
            }
          />
                 {/* ticket/project payroll audit */}
                 <Route
            path="/ticket-payroll-audit"
            element={
              <ProtectedRoute allowedUserTypes={['tasker']}>
                <TicketProjectPayrollAudit/>
              </ProtectedRoute>
            }
          />
                 {/* external payable dashboard */}
                 <Route
            path="/bookkeeping/external-payables"
            element={
              <ProtectedRoute allowedUserTypes={['tasker','manager','general manager']}>
                <ExternalPayablesDashboard/>
              </ProtectedRoute>
            }
          />
                  {/* cash inflow */}
                 <Route
            path="/cash-in"
            element={
              <ProtectedRoute allowedUserTypes={['manager','general manager']}>
                <CashInflow/>
              </ProtectedRoute>
            }
          />
                  {/* cash outflow */}
                 <Route
            path="/cash-out"
            element={
              <ProtectedRoute allowedUserTypes={['tasker']}>
                <CashOutFlow/>
              </ProtectedRoute>
            }
          />
               {/* Discovery - Global Inteligence */}
                 <Route
            path="/discovery"
            element={
              <ProtectedRoute allowedUserTypes={['manager','general manager']}>
                <GrowthIntelligence/>
              </ProtectedRoute>
            }
          />

          {/* Unknown routes render a noindex page. Public namespaces also return HTTP 404 from Express. */}
          <Route path="*" element={<NotFound />} />
        </Routes>
          </Suspense>
        </RouteErrorBoundary>
      </main>

      <Footer />
    </div>
  );
};

const App = () => (
  <UserProvider>
    <QueryClientProvider client={QUERY_CLIENT}>
      <ThemeProvider theme={APP_THEME}>
        <Router>
          <AppContent />
        </Router>
      </ThemeProvider>
    </QueryClientProvider>
  </UserProvider>
);

export default App;

  
